import React, { ChangeEvent, useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import AdminNavBar from "./components/AdminNavBar";
import { Form, Link, useNavigate, useParams } from "react-router-dom";
import FetchError, { fetchErrorMessage } from "../interface/FetchError";
import Loading from "../components/LoadingComponent";
import ErrorComponent from "../components/ErrorComponent";
import { convertToMegabytes } from "./adminHelper";
import { useDropzone } from "react-dropzone";
import apiService from "../services/api.service";

interface BlogPostImage {
  id: number;
  image: string;
}

interface BlogPost {
  id: number;
  category: string;
  author: string;
  social_platform: string;
  social_username: string;
  title: string;
  cover_image: File;
  publish_status: string;
  content_images: BlogPostImage[];
  content: string;
}

const EditBlogPost: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [error, setError] = useState<FetchError>(null);
  const [loading, setLoading] = useState(false);

  const [currentCoverImage, setCurrentCoverImage] = useState("");

  const navigate = useNavigate();
  const handleBackToSiteMaterial = () => {
    navigate("/admin/dashboard/", { state: { from: "blogposts" } });
  };

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const postResponse = await apiService.get<AxiosResponse>(
          `/blogposts/?post_id=${postId}`
        );
        setPosts(postResponse.data);
      } catch (err) {
        if (err instanceof Error) {
          console.error(`${fetchErrorMessage(postId, "blogpost")}: ${err}`);
          setError(err);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const fetchedPost = posts.find(
    (post: any) => post.id === parseInt(postId || "")
  );

  const [blogPost, setBlogPost] = useState({
    title: "",
    cover_image: null as File | null,
    category: "",
    social_platform: "",
    social_username: "",
    content: "",
    publish_status: "",
    content_images: [] as BlogPostImage[],
    uploaded_images: [] as File[],
  });

  useEffect(() => {
    if (fetchedPost) {
      setCurrentCoverImage(
        fetchedPost.cover_image?.toString().split("/").pop() ||
          "No current cover image"
      );
      setBlogPost({
        title: fetchedPost.title,
        cover_image: null,
        category: fetchedPost.category,
        social_platform: fetchedPost.social_platform,
        social_username: fetchedPost.social_username,
        content: fetchedPost.content,
        publish_status: fetchedPost.publish_status,
        content_images: fetchedPost.content_images,
        uploaded_images: [],
      });
    }
  }, [fetchedPost]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setBlogPost({
      ...blogPost,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setBlogPost((prevPost) => ({
        ...prevPost,
        cover_image: file,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("title", blogPost.title);
    formData.append("category", blogPost.category);
    formData.append("social_platform", blogPost.social_platform);
    formData.append("social_username", blogPost.social_username);
    formData.append("content", blogPost.content);
    formData.append("publish_status", blogPost.publish_status);
    if (blogPost.cover_image instanceof File) {
      formData.append("cover_image", blogPost.cover_image);
    }
    blogPost.uploaded_images.forEach((image) => {
      formData.append("uploaded_images", image);
    });

    // make PUT request to api
    const makeUpdateRequest = async () => {
      try {
        await apiService.put(`/blogposts/${postId}/`, formData);
        alert("Post updated successfully");
      } catch (error: any) {
        console.error("Error occured while updating blogpost", error);
        if (error.response && error.response.status === 401) {
          throw new Error("Token expired");
        }
        throw error;
      }
    };

    try {
      await makeUpdateRequest();
    } catch (error: any) {
      console.error("Error occurred while updating blog post", error);
      alert("Failed to update post. Please try again.");
    }
  };

  const makeDeleteRequest = async () => {
    try {
      await apiService.delete(`/blogposts/${postId}/`);
      alert("Post successfully deleted");
      navigate("/admin/site-materials/blogposts");
    } catch (error: any) {
      console.error("Error occured while deleting post", error);
      if (error.response && error.response.status === 401) {
        throw new Error("Token expired");
      }
      throw error;
    }
  };

  const handlePostDelete = async () => {
    const text = "Are you sure you want to delete?";
    if (confirm(text) == true) {
      try {
        await makeDeleteRequest();
      } catch (error: any) {
        console.error("Error occurred while deleting blog post", error);
        alert("Failed to delete post. Please try again.");
      }
    }
  };

  const makeImageDeleteRequest = async (imageId: number) => {
    try {
      await apiService.delete(`/blogpostimages/${imageId.toString()}/`);
      alert("Blog post image successfully deleted");
    } catch (error: any) {
      console.error("Error occured while deleting post image", error);
      if (error.response && error.response.status === 401) {
        throw new Error("Token expired");
      }
      throw error;
    }
  };

  const handleRemoveImage = async (imageId: number, index: number) => {
    try {
      await makeImageDeleteRequest(imageId);
    } catch (error: any) {
      console.error("Error occurred while deleting blog post image", error);
      alert("Failed to delete blog post image. Please try again.");
    }

    setBlogPost((prevBlogPost) => {
      const updatedImages = [...prevBlogPost.content_images];
      updatedImages.splice(index, 1);
      return {
        ...prevBlogPost,
        content_images: updatedImages,
      };
    });
  };

  const onDrop = (acceptedFiles: File[]) => {
    setBlogPost((prevBlogPost) => ({
      ...prevBlogPost,
      uploaded_images: [...prevBlogPost.uploaded_images, ...acceptedFiles],
    }));
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleRemoveAddedImage = (index: number) => {
    setBlogPost((prevBlogPost) => {
      const updatedImages = [...prevBlogPost.uploaded_images];
      updatedImages.splice(index, 1); // Remove the image at the specified index
      return {
        ...prevBlogPost,
        uploaded_images: updatedImages,
      };
    });
  };

  if (loading) return <Loading />;

  if (error) return <ErrorComponent message={error.message} />;

  return (
    <>
      <nav className="navbar navbar-dark bg-dark">
        <AdminNavBar />
      </nav>
      <nav id="bc-nav" aria-label="breadcrumb">
        <ol id="bc-ol" className="breadcrumb">
          <li id="bc-books" className="breadcrumb-item">
            <button
              className="inactive-anchor-link"
              onClick={handleBackToSiteMaterial}
            >
              Site Materials
            </button>{" "}
            <span className="greater-than">&#x02AA2;</span>
          </li>
          <Link
            to={`/admin/site-materials/blogposts`}
            className="breadcrumb-item inactive-anchor-link"
          >
            Blogposts <span className="greater-than">&#x02AA2;</span>
          </Link>
          <li id="bc-active" className="breadcrumb-item active">
            Edit Blogpost
          </li>
        </ol>
      </nav>
      <Form method="post" className="mb-3" onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label"> Title</label>
          <input
            className="form-control"
            type="text"
            name="title"
            value={blogPost.title}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Current Cover Image</label>
          <div className="img-list-container">{currentCoverImage}</div>
        </div>
        <div className="mb-3">
          <label htmlFor="formFileInput" className="form-label">
            New Cover Image
          </label>
          <input
            className={"form-control"}
            id="formFileInput"
            type="file"
            name="cover_image"
            onChange={handleImageChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Category </label>
          <select
            className="form-select"
            name="category"
            value={blogPost.category}
            onChange={handleInputChange}
            required
          >
            <option value="Tutorial">Tutorial</option>
            <option value="News and Updates">News and Updates</option>
            <option value="Tips and Tricks">Tips and Tricks</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Social Plaform</label>
          <select
            className="form-select"
            name="social_platform"
            value={blogPost.social_platform}
            onChange={handleInputChange}
            required
          >
            <option value="Email">Email</option>
            <option value="Website">Website</option>
            <option value="Twitter(x)">Twitter (X)</option>
            <option value="Instagram">Instagram</option>
            <option value="Github">Github</option>
            <option value="Linkedin">LinkedIn</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label">Social UserID</label>
          <input
            className="form-control"
            type="text"
            name="social_username"
            value={blogPost.social_username}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Existing Content Images</label>
          <ul>
            {blogPost.content_images.map((blogPostImage, index) => (
              <li key={blogPostImage.id}>
                {blogPostImage.image.split("/").pop()}
                <button
                  className="btn"
                  id="delete-upload-btn"
                  type="button"
                  onClick={() => handleRemoveImage(blogPostImage.id, index)}
                >
                  &#935;
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="mb-3">
          <label>Add Content Images</label>
          <div id="dropzone" {...getRootProps()}>
            <input {...getInputProps()} />
            <p> Drag 'n' drop image files here, or click to select files </p>
            <div id="dropzone-add"> &#43; </div>
          </div>
        </div>
        <ul>
          {blogPost.uploaded_images.map((file, index) => (
            <li key={index}>
              {file.name} {convertToMegabytes(file.size) + "MB"}
              <button
                className="btn"
                id="delete-upload-btn"
                type="button"
                onClick={() => handleRemoveAddedImage(index)}
              >
                &#935;
              </button>
            </li>
          ))}
        </ul>
        <div className="mb-3">
          <label className="form-label">Content</label>
          <textarea
            className="form-control"
            name="content"
            value={blogPost.content}
            onChange={handleInputChange}
            required
            rows={28}
          />
        </div>
        <div className="mb-3">
          <label className="form-label"> Status </label>
          <select
            className="form-select"
            name="publish_status"
            value={blogPost.publish_status}
            onChange={handleInputChange}
            required
          >
            <option value="Pending Publish">Pending Publish</option>
            <option value="Publish"> Publish </option>
          </select>
        </div>

        <button
          id="create-post-btn"
          className="btn error-page-btn"
          type="submit"
        >
          Update Post
        </button>
      </Form>

      <button
        id="delete-post-btn"
        className="btn btn-danger"
        onClick={handlePostDelete}
      >
        Delete Post
      </button>
    </>
  );
};

export default EditBlogPost;
